import { Equipment } from 'hooks/useEquipment/types';
import { BlueprintFormData } from 'pages/Blueprints/NewBlueprint/types';
import { UseFormReturn } from 'react-hook-form';
import { EQUIPMENT_TYPES } from 'utils/constants';

/**
 * Represents an equipment option for autocomplete components
 */
export interface EquipmentOption {
  /** Equipment ID as string */
  id: string;
  /** Equipment display name */
  name: string;
  kvaValue?: number | null;
}

/**
 * Generates equipment options for autocomplete components
 * @param equipment - Array of equipment items
 * @param equipmentType - Type of equipment to filter (e.g., 'genset', 'battery')
 * @param t - Translation function
 * @returns Array of equipment options with id and name
 */
export const getEquipmentOptions = (
  equipment: Equipment[],
  equipmentType: string,
  t: (key: string) => string
): EquipmentOption[] => {
  if (equipment.length === 0) {
    return [{ id: '', name: t('blueprints:wizard.fields.suggestedRentals.noEquipmentFound') }];
  }
  return equipment
    .filter((item) => item.equipmentType === equipmentType)
    .map((item) => ({ id: item.equipmentId.toString(), name: item.name, kvaValue: item.kvaValue }));
};

/**
 * Gets the appropriate value for autocomplete component based on current form value
 * @param currentValue - Current value from form (number or string)
 * @param equipment - Array of equipment items
 * @param equipmentType - Type of equipment to filter
 * @returns String ID or full EquipmentOption object for display
 */
export const getAutocompleteValue = (
  currentValue: number | string,
  equipment: Equipment[],
  equipmentType: string
): string | EquipmentOption => {
  if (typeof currentValue === 'number') {
    return currentValue.toString();
  }
  if (typeof currentValue === 'string' && currentValue) {
    const equipmentOptions = getEquipmentOptions(equipment, equipmentType, () => '');
    return equipmentOptions.find((option) => option.id === currentValue) || currentValue;
  }
  return currentValue || '';
};

/**
 * Gets the display label for an autocomplete option
 * @param option - Option value (string or EquipmentOption object)
 * @returns Display string for the option
 */
export const getOptionLabel = ({ option, isKvaValueShown = false }): string => {
  if (typeof option === 'string') {
    return option;
  }
  return isKvaValueShown ? `${option.name} (${option.kvaValue ?? '-'} kVA)` : option.name;
};

/**
 * Compares two autocomplete options for equality by extracting their IDs
 * Handles all combinations of string and EquipmentOption types
 * @param option - First option to compare (string ID or EquipmentOption object)
 * @param value - Second option to compare (string ID or EquipmentOption object)
 * @returns True if the IDs are equal, false otherwise
 */
export const isOptionEqualToValue = (option: string | EquipmentOption, value: string | EquipmentOption): boolean => {
  const optionId = typeof option === 'string' ? option : option.id;
  const valueId = typeof value === 'string' ? value : value.id;

  return optionId === valueId;
};

/**
 * Handles autocomplete selection change
 * @param value - Selected value (string or EquipmentOption object)
 * @param formMethods - React Hook Form methods
 * @param fieldName - Name of the form field to update
 */
export const handleAutocompleteChange = (
  value: string | EquipmentOption | null,
  formMethods: UseFormReturn<BlueprintFormData>,
  fieldName: string
) => {
  const newValue = typeof value === 'object' ? value?.id || '' : value || '';
  formMethods.setValue(fieldName as keyof BlueprintFormData, newValue, { shouldValidate: true });
};

/**
 * Find battery with "422" in name
 * @param batteries - Array of battery equipment
 * @returns Battery equipment with "422" in name, or undefined if not found
 */
export const findBatteryWith422 = (batteries: Equipment[]): Equipment | undefined => {
  return batteries.find((eq) => eq.name.toLowerCase().includes('422'));
};

/**
 * Calculate battery count based on minimum requirement
 * @param battery - Battery equipment
 * @param batteryMinKwh - Minimum required battery capacity in kWh
 * @returns Number of batteries needed (at least 1)
 */
export const calculateBatteryCount = (battery: Equipment, batteryMinKwh?: number): number => {
  if (!batteryMinKwh || !battery.kwhCapacity || battery.kwhCapacity <= 0) {
    return 1;
  }
  return Math.ceil(batteryMinKwh / battery.kwhCapacity);
};

/**
 * Find optimal genset combination that meets minimum kVA requirement with minimal excess capacity
 * @param gensets - Array of genset equipment
 * @param gensetMinKva - Minimum required genset capacity in kVA
 * @returns Object with selected genset and count, or undefined if none found
 */
export const findOptimalGenset = (
  gensets: Equipment[],
  gensetMinKva?: number
): { genset: Equipment; count: number } | undefined => {
  // Filter valid gensets
  const eligibleGensets = gensets.filter(
    (eq) => eq.equipmentType === EQUIPMENT_TYPES.GENSET && eq.kvaValue != null && eq.kvaValue > 0
  );

  if (eligibleGensets.length === 0 || !gensetMinKva) {
    // If no requirement, return smallest genset
    if (!gensetMinKva && eligibleGensets.length > 0) {
      const smallest = eligibleGensets.reduce((smallest, current) => {
        const smallestKva = smallest.kvaValue || Infinity;
        const currentKva = current.kvaValue || Infinity;
        return currentKva < smallestKva ? current : smallest;
      });
      return { genset: smallest, count: 1 };
    }
    return undefined;
  }

  // Sort gensets by kVA (smallest first) to prefer smaller units
  const sortedGensets = [...eligibleGensets].sort((a, b) => (a.kvaValue || 0) - (b.kvaValue || 0));

  let bestOption: { genset: Equipment; count: number; excess: number } | null = null;

  // Try each genset and calculate how many we need
  for (const genset of sortedGensets) {
    const kvaValue = genset.kvaValue || 0;
    if (kvaValue <= 0) {
      continue;
    }

    const count = Math.ceil(gensetMinKva / kvaValue);
    const totalKva = kvaValue * count;
    const excess = totalKva - gensetMinKva;

    // If this is the first option or has less excess, or same excess but smaller genset
    if (
      !bestOption ||
      excess < bestOption.excess ||
      (excess === bestOption.excess && kvaValue < (bestOption.genset.kvaValue || 0))
    ) {
      bestOption = { genset, count, excess };
    }
  }

  return bestOption ? { genset: bestOption.genset, count: bestOption.count } : undefined;
};

/**
 * Calculate genset count based on minimum requirement
 * @param genset - Genset equipment
 * @param gensetMinKva - Minimum required genset capacity in kVA
 * @returns Number of gensets needed (at least 1)
 */
export const calculateGensetCount = (genset: Equipment, gensetMinKva?: number): number => {
  if (!gensetMinKva || !genset.kvaValue || genset.kvaValue <= 0) {
    return 1;
  }
  return Math.ceil(gensetMinKva / genset.kvaValue);
};
